<template>
  <div class="min-h-screen bg-white">
    <!-- ═══════════════════════════════════════════════════════════════ -->
    <!-- HERO — warm, aspirational, human-first                         -->
    <!-- ═══════════════════════════════════════════════════════════════ -->
    <section class="relative overflow-hidden bg-gradient-to-br from-ink-900 via-ink-800 to-ink-900 text-white">
      <!-- soft ambient glow -->
      <div class="absolute inset-0 opacity-[0.07]">
        <div class="absolute -top-20 -left-20 w-[500px] h-[500px] bg-brand-300 rounded-full blur-[120px]"></div>
        <div class="absolute -bottom-32 -right-20 w-[600px] h-[600px] bg-brand-400 rounded-full blur-[140px]"></div>
        <div class="absolute top-1/2 left-1/3 w-[300px] h-[300px] bg-warn-500 rounded-full blur-[100px]"></div>
      </div>

      <div class="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-32 relative z-10">
        <div class="max-w-2xl">
          <!-- mini badge -->
          <div class="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/[0.08] border border-white/[0.06] text-brand-300 text-xs font-semibold tracking-wide mb-8 backdrop-blur-sm">
            <span class="relative flex h-2 w-2">
              <span class="animate-ping absolute inline-flex h-full w-full rounded-full bg-brand-400 opacity-75"></span>
              <span class="relative inline-flex rounded-full h-2 w-2 bg-brand-400"></span>
            </span>
            Partner Program — earn monthly
          </div>

          <h1 class="text-4xl sm:text-5xl md:text-6xl font-display font-bold tracking-tight leading-[1.08] text-white">
            Turn your
            <span class="text-brand-400">audience</span>
            into income
          </h1>

          <p class="mt-6 text-lg md:text-xl text-white/65 leading-relaxed max-w-lg">
            You already recommend tools to your audience. Start getting paid for it — <span class="text-white/85">recurring monthly commission</span> on every customer you send our way. No catches, no caps.
          </p>

          <!-- mini social-proof row -->
          <div class="mt-6 flex items-center gap-6 text-sm text-white/40">
            <div class="flex -space-x-2">
              <div class="w-8 h-8 rounded-full bg-brand-500/30 border-2 border-ink-900 flex items-center justify-center text-[10px] font-bold text-brand-300">JD</div>
              <div class="w-8 h-8 rounded-full bg-brand-500/20 border-2 border-ink-900 flex items-center justify-center text-[10px] font-bold text-brand-300">MK</div>
              <div class="w-8 h-8 rounded-full bg-brand-500/10 border-2 border-ink-900 flex items-center justify-center text-[10px] font-bold text-brand-300">+</div>
            </div>
            <span>Trusted by SEO consultants &amp; agency owners</span>
          </div>

          <!-- CTA cluster -->
          <div class="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <router-link
              to="/affiliates/apply"
              class="group inline-flex items-center gap-2.5 px-7 py-4 rounded-2xl text-base font-semibold bg-brand-500 hover:bg-brand-400 text-white transition-all duration-200 shadow-xl shadow-brand-500/20 hover:shadow-brand-500/30 hover:-translate-y-0.5"
            >
              Apply to partner program
              <Icon name="arrow-right" :size="18" class="group-hover:translate-x-0.5 transition-transform" />
            </router-link>
            <a href="#how-it-works" class="group inline-flex items-center gap-2 px-7 py-4 rounded-2xl text-base font-semibold text-white bg-ink-800/70 border border-ink-600 hover:bg-ink-700 hover:border-ink-500 transition-all duration-200 btn-glow">
              See how it works
              <Icon name="chevron-down" :size="16" />
            </a>
          </div>

          <!-- tiny trust line -->
          <div class="mt-5 flex flex-wrap items-center gap-x-5 gap-y-1 text-xs text-white/35">
            <span class="flex items-center gap-1"><Icon name="check-circle" :size="12" class="text-brand-500/60" /> Free to join</span>
            <span class="flex items-center gap-1"><Icon name="check-circle" :size="12" class="text-brand-500/60" /> Monthly PayPal payouts</span>
            <span class="flex items-center gap-1"><Icon name="check-circle" :size="12" class="text-brand-500/60" /> 30-day tracking cookie</span>
          </div>
        </div>
      </div>
    </section>

    <!-- ═══════════════════════════════════════════════════════════════ -->
    <!-- HOW IT WORKS — visual, warm, with earning examples              -->
    <!-- ═══════════════════════════════════════════════════════════════ -->
    <section id="how-it-works" class="py-24 md:py-32 bg-white">
      <div class="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="text-center mb-16">
          <span class="inline-block px-3 py-1 rounded-full bg-brand-50 text-brand-700 text-xs font-semibold tracking-wide mb-4">Simple process</span>
          <h2 class="text-3xl md:text-4xl font-display font-bold text-ink-900">Start earning in three steps</h2>
          <p class="mt-3 text-ink-500 max-w-lg mx-auto">It takes less than 5 minutes to apply. We approve most applications within 24 hours.</p>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-3 gap-10 lg:gap-14">
          <!-- Step 1 -->
          <div class="relative group">
            <div class="absolute -top-2 -left-2 w-12 h-12 rounded-xl bg-brand-100 text-brand-600 flex items-center justify-center text-xl font-bold font-display group-hover:bg-brand-200 group-hover:scale-105 transition-all">
              1
            </div>
            <div class="pl-16">
              <div class="w-12 h-12 rounded-xl bg-brand-50 text-brand-600 flex items-center justify-center mb-4 group-hover:bg-brand-100 transition-colors">
                <Icon name="edit" :size="22" />
              </div>
              <h3 class="text-lg font-semibold text-ink-900 mb-2">Tell us about yourself</h3>
              <p class="text-sm text-ink-500 leading-relaxed">
                A quick form about your audience and how you'd promote LeadRanks. We review it personally — no bots, no auto-rejections.
              </p>
              <p class="mt-3 text-xs text-ink-400 flex items-center gap-1.5">
                <Icon name="clock" :size="12" />
                Takes ~3 minutes
              </p>
            </div>
          </div>

          <!-- Step 2 -->
          <div class="relative group">
            <div class="absolute -top-2 -left-2 w-12 h-12 rounded-xl bg-brand-100 text-brand-600 flex items-center justify-center text-xl font-bold font-display group-hover:bg-brand-200 group-hover:scale-105 transition-all">
              2
            </div>
            <div class="pl-16">
              <div class="w-12 h-12 rounded-xl bg-brand-50 text-brand-600 flex items-center justify-center mb-4 group-hover:bg-brand-100 transition-colors">
                <Icon name="share-2" :size="22" />
              </div>
              <h3 class="text-lg font-semibold text-ink-900 mb-2">Get your link &amp; share</h3>
              <p class="text-sm text-ink-500 leading-relaxed">
                Once approved, you get a unique tracking link and access to banners, copy templates, and a real-time dashboard. Share anywhere.
              </p>
              <p class="mt-3 text-xs text-ink-400 flex items-center gap-1.5">
                <Icon name="check-circle" :size="12" class="text-brand-500" />
                Marketing assets included
              </p>
            </div>
          </div>

          <!-- Step 3 -->
          <div class="relative group">
            <div class="absolute -top-2 -left-2 w-12 h-12 rounded-xl bg-brand-100 text-brand-600 flex items-center justify-center text-xl font-bold font-display group-hover:bg-brand-200 group-hover:scale-105 transition-all">
              3
            </div>
            <div class="pl-16">
              <div class="w-12 h-12 rounded-xl bg-brand-50 text-brand-600 flex items-center justify-center mb-4 group-hover:bg-brand-100 transition-colors">
                <Icon name="dollar-sign" :size="22" />
              </div>
              <h3 class="text-lg font-semibold text-ink-900 mb-2">Watch your earnings grow</h3>
              <p class="text-sm text-ink-500 leading-relaxed">
                Every sign-up from your link earns you <strong class="text-ink-800">recurring monthly commission</strong>. Dashboard updates in real-time. Cash out monthly via PayPal.
              </p>
              <p class="mt-3 text-xs text-ink-400 flex items-center gap-1.5">
                <Icon name="check-circle" :size="12" class="text-brand-500" />
                $50 minimum payout
              </p>
            </div>
          </div>
        </div>

        <!-- Earning example -->
        <div class="mt-16 bg-gradient-to-br from-brand-50 to-warn-50 rounded-2xl border border-brand-100/60 p-6 md:p-8 max-w-2xl mx-auto">
          <div class="flex items-start gap-4">
            <div class="w-10 h-10 rounded-xl bg-white text-brand-600 flex items-center justify-center shrink-0 shadow-sm">
              <Icon name="zap" :size="20" />
            </div>
            <div>
              <h4 class="font-semibold text-ink-900">What you could earn</h4>
              <p class="mt-2 text-sm text-ink-600 leading-relaxed">
                If you refer just <strong>10 customers</strong> on a $49/mo plan, that's
                <strong class="text-brand-700">$98–$147/mo</strong> in passive commission — every month, as long as they stay.
                Refer 50 and you're looking at <strong class="text-brand-700">$500–$735/mo</strong>.
              </p>
              <div class="mt-4 flex flex-wrap gap-3">
                <span class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white text-xs font-medium text-ink-700 border border-ink-100">
                  <Icon name="users" :size="13" /> 10 referrals ≈ $122/mo
                </span>
                <span class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white text-xs font-medium text-ink-700 border border-ink-100">
                  <Icon name="users" :size="13" /> 25 referrals ≈ $306/mo
                </span>
                <span class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white text-xs font-medium text-ink-700 border border-ink-100">
                  <Icon name="users" :size="13" /> 50 referrals ≈ $612/mo
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ═══════════════════════════════════════════════════════════════ -->
    <!-- WHO IT'S FOR — personal, relatable segments                    -->
    <!-- ═══════════════════════════════════════════════════════════════ -->
    <section class="py-24 md:py-28 bg-ink-50">
      <div class="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="text-center mb-14">
          <h2 class="text-3xl md:text-4xl font-display font-bold text-ink-900">Perfect for people like you</h2>
          <p class="mt-3 text-ink-500 max-w-lg mx-auto">If any of these sound familiar, you're in the right place.</p>
        </div>

        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          <!-- SEO consultant -->
          <div class="bg-white rounded-2xl border border-ink-100 p-6 hover:border-brand-200 hover:shadow-lg hover:shadow-brand-50 transition-all duration-200 group">
            <div class="w-10 h-10 rounded-xl bg-brand-50 text-brand-700 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
              <Icon name="trending-up" :size="20" />
            </div>
            <h3 class="font-semibold text-ink-900 mb-2">SEO consultants</h3>
            <p class="text-sm text-ink-500 leading-relaxed">You audit client sites. When you find issues, recommend LeadRanks and earn from every sign-up.</p>
          </div>

          <!-- Blogger / Creator -->
          <div class="bg-white rounded-2xl border border-ink-100 p-6 hover:border-brand-200 hover:shadow-lg hover:shadow-brand-50 transition-all duration-200 group">
            <div class="w-10 h-10 rounded-xl bg-accent-50 text-accent-700 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
              <Icon name="edit" :size="20" />
            </div>
            <h3 class="font-semibold text-ink-900 mb-2">Content creators</h3>
            <p class="text-sm text-ink-500 leading-relaxed">Write about SEO, marketing, or blogging? Drop your link in relevant posts and earn passively.</p>
          </div>

          <!-- Agency owner -->
          <div class="bg-white rounded-2xl border border-ink-100 p-6 hover:border-brand-200 hover:shadow-lg hover:shadow-brand-50 transition-all duration-200 group">
            <div class="w-10 h-10 rounded-xl bg-brand-50 text-brand-700 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
              <Icon name="briefcase" :size="20" />
            </div>
            <h3 class="font-semibold text-ink-900 mb-2">Agency owners</h3>
            <p class="text-sm text-ink-500 leading-relaxed">White-label LeadRanks for your clients and earn recurring commission — double win for your bottom line.</p>
          </div>

          <!-- Newsletter / social -->
          <div class="bg-white rounded-2xl border border-ink-100 p-6 hover:border-brand-200 hover:shadow-lg hover:shadow-brand-50 transition-all duration-200 group">
            <div class="w-10 h-10 rounded-xl bg-accent-50 text-accent-700 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
              <Icon name="mail" :size="20" />
            </div>
            <h3 class="font-semibold text-ink-900 mb-2">Newsletter authors</h3>
            <p class="text-sm text-ink-500 leading-relaxed">Got a newsletter about SEO, SaaS, or startups? A single mention can drive dozens of sign-ups.</p>
          </div>
        </div>

        <!-- don't-fit box -->
        <div class="mt-8 text-center">
          <p class="text-sm text-ink-400">
            Don't fit a box? Doesn't matter. <span class="text-ink-600">If you have an audience that needs SEO tools, you can earn.</span>
          </p>
        </div>
      </div>
    </section>

    <!-- ═══════════════════════════════════════════════════════════════ -->
    <!-- TIERS — kept from original (it was good), lightly warmed up     -->
    <!-- ═══════════════════════════════════════════════════════════════ -->
    <section class="py-24 md:py-28 bg-white">
      <div class="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="text-center mb-16">
          <span class="inline-block px-3 py-1 rounded-full bg-warn-50 text-warn-700 text-xs font-semibold tracking-wide mb-4">Commission tiers</span>
          <h2 class="text-3xl md:text-4xl font-display font-bold text-ink-900">The more you earn, the more we share</h2>
          <p class="mt-3 text-ink-500 max-w-lg mx-auto">Start at 20%. Your rate climbs automatically as your earnings grow — no renegotiation needed.</p>
        </div>
        <div class="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
          <!-- Standard -->
          <div class="bg-white rounded-2xl border border-ink-100 p-8 relative hover:border-brand-200 hover:shadow-md transition-all duration-200">
            <div class="text-xs font-bold text-ink-400 uppercase tracking-wider mb-3">Standard</div>
            <div class="text-4xl font-display font-bold text-ink-900 mb-1">20%</div>
            <div class="text-sm text-ink-500 mb-6">Recurring commission</div>
            <div class="h-px bg-ink-100 mb-5"></div>
            <ul class="space-y-3 text-sm text-ink-600">
              <li class="flex gap-2"><Icon name="check-circle" :size="16" classes="text-brand-500 mt-0.5 shrink-0" /> Up to $500/mo in earnings</li>
              <li class="flex gap-2"><Icon name="check-circle" :size="16" classes="text-brand-500 mt-0.5 shrink-0" /> 30-day tracking cookie</li>
              <li class="flex gap-2"><Icon name="check-circle" :size="16" classes="text-brand-500 mt-0.5 shrink-0" /> Real-time dashboard</li>
              <li class="flex gap-2"><Icon name="check-circle" :size="16" classes="text-brand-500 mt-0.5 shrink-0" /> Marketing assets</li>
            </ul>
          </div>
          <!-- Silver -->
          <div class="bg-white rounded-2xl border-2 border-brand-300 p-8 relative ring-4 ring-brand-50 hover:shadow-lg transition-all duration-200">
            <div class="absolute -top-3 left-1/2 -translate-x-1/2 bg-brand-500 text-white text-xs font-bold px-3 py-1 rounded-full">Most popular</div>
            <div class="text-xs font-bold text-brand-600 uppercase tracking-wider mb-3">Silver</div>
            <div class="text-4xl font-display font-bold text-ink-900 mb-1">25%</div>
            <div class="text-sm text-ink-500 mb-6">At $500+/mo earnings</div>
            <div class="h-px bg-ink-100 mb-5"></div>
            <ul class="space-y-3 text-sm text-ink-600">
              <li class="flex gap-2"><Icon name="check-circle" :size="16" classes="text-brand-500 mt-0.5 shrink-0" /> Everything in Standard</li>
              <li class="flex gap-2"><Icon name="check-circle" :size="16" classes="text-brand-500 mt-0.5 shrink-0" /> Priority support</li>
              <li class="flex gap-2"><Icon name="check-circle" :size="16" classes="text-brand-500 mt-0.5 shrink-0" /> Custom landing pages</li>
              <li class="flex gap-2"><Icon name="check-circle" :size="16" classes="text-brand-500 mt-0.5 shrink-0" /> Co-branded content</li>
            </ul>
          </div>
          <!-- Gold -->
          <div class="bg-white rounded-2xl border border-ink-100 p-8 relative hover:border-warn-100 hover:shadow-md transition-all duration-200">
            <div class="text-xs font-bold text-warn-600 uppercase tracking-wider mb-3">Gold</div>
            <div class="text-4xl font-display font-bold text-ink-900 mb-1">30%</div>
            <div class="text-sm text-ink-500 mb-6">At $2,000+/mo earnings</div>
            <div class="h-px bg-ink-100 mb-5"></div>
            <ul class="space-y-3 text-sm text-ink-600">
              <li class="flex gap-2"><Icon name="check-circle" :size="16" classes="text-brand-500 mt-0.5 shrink-0" /> Everything in Silver</li>
              <li class="flex gap-2"><Icon name="check-circle" :size="16" classes="text-brand-500 mt-0.5 shrink-0" /> Dedicated partner manager</li>
              <li class="flex gap-2"><Icon name="check-circle" :size="16" classes="text-brand-500 mt-0.5 shrink-0" /> Early product access</li>
              <li class="flex gap-2"><Icon name="check-circle" :size="16" classes="text-brand-500 mt-0.5 shrink-0" /> Joint webinars & events</li>
            </ul>
          </div>
        </div>
      </div>
    </section>

    <!-- ═══════════════════════════════════════════════════════════════ -->
    <!-- STATS — tighter, more human                                     -->
    <!-- ═══════════════════════════════════════════════════════════════ -->
    <section class="py-20 bg-gradient-to-r from-ink-900 via-ink-800 to-ink-900 text-white">
      <div class="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 text-center">
          <div class="group">
            <div class="text-4xl font-display font-bold text-brand-400 group-hover:scale-105 transition-transform">20–30%</div>
            <div class="mt-2 text-sm text-white/55">Recurring commission</div>
          </div>
          <div class="group">
            <div class="text-4xl font-display font-bold text-brand-400 group-hover:scale-105 transition-transform">30 days</div>
            <div class="mt-2 text-sm text-white/55">Cookie window</div>
          </div>
          <div class="group">
            <div class="text-4xl font-display font-bold text-brand-400 group-hover:scale-105 transition-transform">$50</div>
            <div class="mt-2 text-sm text-white/55">Minimum payout</div>
          </div>
          <div class="group">
            <div class="text-4xl font-display font-bold text-brand-400 group-hover:scale-105 transition-transform">Monthly</div>
            <div class="mt-2 text-sm text-white/55">PayPal payouts</div>
          </div>
        </div>
      </div>
    </section>

    <!-- ═══════════════════════════════════════════════════════════════ -->
    <!-- FAQ + warm bottom CTA                                          -->
    <!-- ═══════════════════════════════════════════════════════════════ -->
    <section class="py-24 md:py-32 bg-ink-50">
      <div class="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="text-center mb-14">
          <h2 class="text-3xl md:text-4xl font-display font-bold text-ink-900">Questions? We've got answers.</h2>
        </div>
        <div class="space-y-3">
          <details class="bg-white rounded-xl border border-ink-100 p-5 group cursor-pointer">
            <summary class="flex items-center justify-between font-semibold text-ink-800 text-sm select-none">Who can join? Is there an approval process?</summary>
            <p class="mt-3 text-sm text-ink-600 leading-relaxed">Anyone with an audience in SEO, digital marketing, or web development. We review each application personally — not to gatekeep, but to make sure we can support you well. Most applicants hear back within 24 hours.</p>
          </details>
          <details class="bg-white rounded-xl border border-ink-100 p-5 group cursor-pointer">
            <summary class="flex items-center justify-between font-semibold text-ink-800 text-sm select-none">How does the 30-day cookie work?</summary>
            <p class="mt-3 text-sm text-ink-600 leading-relaxed">When someone clicks your link, a cookie is stored for 30 days. If they sign up anytime in that window — even days later — you get the credit. No last-click shenanigans, just fair attribution.</p>
          </details>
          <details class="bg-white rounded-xl border border-ink-100 p-5 group cursor-pointer">
            <summary class="flex items-center justify-between font-semibold text-ink-800 text-sm select-none">When and how do I get paid?</summary>
            <p class="mt-3 text-sm text-ink-600 leading-relaxed">Monthly PayPal payouts once you hit $50. You can request a payout anytime from your dashboard — no waiting for quarterly checks or chasing invoices.</p>
          </details>
          <details class="bg-white rounded-xl border border-ink-100 p-5 group cursor-pointer">
            <summary class="flex items-center justify-between font-semibold text-ink-800 text-sm select-none">I run an agency — can I white-label this?</summary>
            <p class="mt-3 text-sm text-ink-600 leading-relaxed">Yes. If you're on Silver tier or above, we can set up co-branded or white-label landing pages for your referrals. Talk to us after you're approved — we'll make it happen.</p>
          </details>
        </div>

        <!-- Big warm CTA box -->
        <div class="mt-16 text-center bg-gradient-to-br from-brand-500 to-brand-600 rounded-2xl p-10 md:p-14 shadow-xl shadow-brand-500/20">
          <h3 class="text-2xl md:text-3xl font-display font-bold text-white mb-4">Ready to turn your audience into income?</h3>
          <p class="text-brand-100 mb-8 max-w-md mx-auto text-sm md:text-base leading-relaxed">
            Takes 3 minutes to apply. Most partners hear back within 24 hours. No cost, no risk, no catch.
          </p>
          <router-link
            to="/affiliates/apply"
            class="inline-flex items-center gap-2.5 px-8 py-4 rounded-2xl text-base font-semibold bg-white text-brand-700 hover:bg-brand-50 transition-all duration-200 shadow-lg hover:shadow-xl hover:-translate-y-0.5"
          >
            Apply to partner program
            <Icon name="arrow-right" :size="18" />
          </router-link>
          <p class="mt-4 text-brand-200/70 text-xs">Free to join · Monthly payouts · Cancel anytime</p>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
import { usePageMeta } from '../../composables/usePageMeta.js';
import Icon from '../ui/Icon.vue';

usePageMeta({
  title: 'Partner Program — LeadRanks',
  meta: [
    { name: 'description', content: 'Earn recurring commission referring agencies and freelancers to LeadRanks. Transparent tracking, monthly payouts, and marketing assets ready to use.' },
    { property: 'og:title', content: 'Partner Program — LeadRanks' },
    { property: 'og:description', content: 'Earn recurring commission referring agencies and freelancers to LeadRanks. Transparent tracking, monthly payouts, and marketing assets ready to use.' },
    { property: 'og:image', content: 'https://leadranks.io/og-image-1200x627.png' },
    { name: 'twitter:card', content: 'summary_large_image' },
  ],
});
</script>
